import { FC, ReactNode, useState } from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import { yupResolver } from '@hookform/resolvers/yup';
import { Stack, Typography } from '@mui/material';

import { getSubmitDisabled } from '@mopla/utils';

import { DriverPasswordInput } from '../../atoms/DriverPasswordInput/DriverPasswordInput';
import { MplButton } from '../../atoms/MplButton/MplButton';
import { CompleteAnim } from '../../molecules/Animations';
import { DropdownIcon } from '../../molecules/DropdownIcon/DropdownIcon';
import {
	DRIVER_ANDROID_LINK,
	DRIVER_IOS_LINK,
} from '../../molecules/StoreBadges/constants';
import { StoreBadges } from '../../molecules/StoreBadges/StoreBadges';
import { PasswordValidationConditions } from '../../validation/PasswordValidationConditions';
import {
	BrowserTitle,
	LoginBox,
} from '../ChangePasswordForm/ChangePasswordForm.styles';
import { ChangePasswordValidationSchema } from '../ChangePasswordForm/ChangePasswordValidation';

import {
	Description,
	ErrorWrapper,
	Root,
	SubmitLevel,
	Title,
} from './DriverChangePasswordForm.styles';

interface IFormInput {
	password: string;
	confirmPassword: string;
}

interface DriverChangePasswordProps {
	showSuccess: boolean;
	onSuccessCallback: () => void;
	onSubmitCallback: () => void;
	onErrorCallback: () => void;
	isOnboarding?: boolean;
	verifyPasswordResetCode: any;
	confirmPasswordReset: any;
	getAuth: any;
	driverLoginForm?: ReactNode;
}

export const DriverChangePasswordForm: FC<DriverChangePasswordProps> = ({
	onSuccessCallback,
	showSuccess,
	onSubmitCallback,
	onErrorCallback,
	isOnboarding,
	verifyPasswordResetCode,
	confirmPasswordReset,
	getAuth,
	driverLoginForm,
}) => {
	const { t } = useTranslation([
		'changePassword',
		'validation',
		'registration',
	]);
	const [error, setError] = useState(false);
	const [showLogin, setShowLogin] = useState(false);
	const location = useLocation();

	const methods = useForm<IFormInput>({
		defaultValues: {
			password: '',
			confirmPassword: '',
		},
		resolver: yupResolver(ChangePasswordValidationSchema),
		mode: 'onTouched',
	});
	const {
		formState: { errors, touchedFields },
		handleSubmit,
		watch,
	} = methods;

	const passwordValue = watch('password');

	const onSubmit = async (data: IFormInput) => {
		if (error) {
			onErrorCallback();
		}
		const { password } = data;
		const auth = getAuth();
		const actionCode = new URLSearchParams(location.search).get('oobCode');
		try {
			await verifyPasswordResetCode(auth, actionCode as string);
			await confirmPasswordReset(auth, actionCode as string, password);
			setError(false);
			onSuccessCallback();
			methods.reset({ password: '' });
		} catch {
			setError(true);
		}
	};

	if (showSuccess) {
		if (!Capacitor.isNativePlatform()) {
			return (
				<Stack className="content" gap={3}>
					<CompleteAnim loop />
					<Title>
						{t(
							isOnboarding
								? 'success_screen.text.description_driver'
								: 'success_screen.title'
						)}
					</Title>
					<Stack gap={2}>
						<Typography variant="body2" color="#66758F" textAlign="center">
							{t('success_screen.text.description_driver')}
						</Typography>
						<BrowserTitle>{t('text.easier')}</BrowserTitle>
						<Typography variant="body2" mb={2}>
							{t('text.download_app')}
						</Typography>
						<StoreBadges
							iosLink={DRIVER_IOS_LINK}
							androidLink={DRIVER_ANDROID_LINK}
						/>
						{driverLoginForm && (
							<>
								<LoginBox onClick={() => setShowLogin((p) => !p)}>
									<span>{t('text.login_in_browser')}</span>
									<DropdownIcon isOpened={showLogin} />
								</LoginBox>
								{showLogin && driverLoginForm}
							</>
						)}
					</Stack>
				</Stack>
			);
		}

		return (
			<Stack
				className="content"
				gap={3}
				data-testid="change-passwordForm-success-screen"
			>
				<CompleteAnim loop />
				<Title>
					{t(
						isOnboarding
							? 'success_screen.text.description_driver'
							: 'success_screen.title'
					)}
				</Title>
				<Typography variant="body2" color="#66758F" textAlign="center">
					{t('success_screen.text.description')}
				</Typography>
				<SubmitLevel>
					<MplButton
						fullWidth
						color="secondary"
						data-testid="successScreen_button_toLogin"
						onClick={onSubmitCallback}
					>
						{t('success_screen.button.to_the_login')}
					</MplButton>
				</SubmitLevel>
			</Stack>
		);
	}

	const getButtonName = () => {
		if (error) return t('success_screen.button.to_login');
		return t('button.change_password');
	};

	return (
		<>
			<Title>{t(isOnboarding ? 'text.title_driver' : 'text.title')}</Title>
			<Description>{t('text.description')}</Description>
			<FormProvider {...methods}>
				<form
					id="changePasswordForm"
					onSubmit={handleSubmit((data) => onSubmit(data))}
				>
					<Root>
						<Stack gap={2}>
							<Controller
								name="password"
								control={methods.control}
								render={({ field }) => (
									<DriverPasswordInput
										{...field}
										label={t('label.password')}
										error={Boolean(errors.password?.message || error)}
										helperText={
											errors.password &&
											t(`validation:${errors.password.message}`)
										}
										inputProps={{
											'data-testid': 'change-passwordForm-password-input',
										}}
										autoComplete="new-password"
									/>
								)}
							/>
							<ErrorWrapper error={error}>{t('text.expired')}</ErrorWrapper>
							<PasswordValidationConditions
								driverApp
								password={passwordValue}
								fieldTouched={touchedFields.password}
								checkPasswordErrors={Boolean(
									touchedFields.password && errors.password
								)}
							/>
							<Controller
								name="confirmPassword"
								control={methods.control}
								render={({ field }) => (
									<DriverPasswordInput
										{...field}
										label={t('label.confirmPassword')}
										helperText={
											Boolean(errors.confirmPassword?.message) &&
											t(`validation:${errors.confirmPassword?.message}`)
										}
										error={Boolean(errors.confirmPassword?.message || error)}
										inputProps={{
											'data-testid':
												'change-passwordForm-confirmPassword-input',
										}}
										autoComplete="new-password"
									/>
								)}
							/>
						</Stack>
						<SubmitLevel>
							<MplButton
								fullWidth
								color="secondary"
								data-testid="forgot-password-button"
								disabled={getSubmitDisabled(methods, {
									whenInternallyInvalid: true,
								})}
								type="submit"
								form="changePasswordForm"
							>
								{getButtonName()}
							</MplButton>
						</SubmitLevel>
					</Root>
				</form>
			</FormProvider>
		</>
	);
};
